import {
  ReactElement,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { Alert } from "@justworkshr/milo-core";

import { StepsEnum } from "pages/register/types/registrationSteps";
import {
  UserRegistrationForm,
  TextInputField,
} from "pages/register/types/userRegistrationForm";

import PageAlertContext from "contexts/PageAlertContext";
import ToggleMfaStepContext from "../../../../contexts/ToggleMfaStepContext";

import useCreateAndAuthUser from "pages/register/hooks/useCreateAndAuthUser";
import useInlineErrorStateManager from "pages/register/hooks/useInlineErrorStateManager";
import useRegistrationInputValidationManager from "pages/register/hooks/useRegistrationInputValidationManager";

import AuthPageTemplate from "../authPageTemplate/AuthPageTemplate";
import NameAndEmailForm from "../nameAndEmailForm/NameAndEmailForm";
import LoginCredentialsForm from "../loginCredentialsForm/LoginCredentialsForm";

import getDefaultUserRegistrationData from "pages/register/util/getDefaultUserRegistrationData";
import parseCreateUserError, {
  ErrorFlags,
} from "pages/register/util/apiErrors/parseCreateUserError";

import styles from "./registration-forms.module.css";

const PageContent = {
  [StepsEnum.You]: {
    title: "Name & email",
    description: "Nice to meet you. Tell us a little about yourself.",
  },
  [StepsEnum.LoginInfo]: {
    title: "Create your login info",
    description:
      "This is the username and password you’ll use for all things Justworks.",
  },
};

const RegistrationForms = (): ReactElement => {
  const [isOnBasicInfoForm, setIsOnBasicInfoForm] = useState(true);
  const [formData, setFormData] = useState<UserRegistrationForm>({
    ...getDefaultUserRegistrationData(),
  });
  const [apiErrorFlags, setApiErrorFlags] = useState<ErrorFlags | null>(null);
  const [showCommonPasswordWarning, setShowCommonPasswordWarning] =
    useState(false);
  const navigate = useNavigate();
  const {
    mutate: createAndAuthUser,
    isLoading: isSubmittingForm,
    userCreationError: userCreationErrorResponse,
  } = useCreateAndAuthUser();
  const content = isOnBasicInfoForm
    ? PageContent[StepsEnum.You]
    : PageContent[StepsEnum.LoginInfo];

  const { setAlert, removeAlert } = useContext(PageAlertContext);
  const { mfaStepEnabled } = useContext(ToggleMfaStepContext);

  const onCompleteFirstForm = () => {
    setIsOnBasicInfoForm(false);
    removeAlert();
  };
  const onCompleteLoginForm = () => {
    clearAllInlineErrors();
    setApiErrorFlags(null);
    createAndAuthUser(formData, mfaStepEnabled);
  };

  const { inputValidationMap, updateInputValidation, clearInputValidation } =
    useRegistrationInputValidationManager();
  const {
    inlineErrorState,
    updateInlineErrorStateWithDelay,
    setInlineErrorState,
    clearInlineError,
    clearAllInlineErrors,
  } = useInlineErrorStateManager();

  const onChangeInput = (inputField: TextInputField, value: string) => {
    if (!value) {
      clearInlineError(inputField);
    }

    setFormData((prevData) => ({
      ...prevData,
      [inputField]: value,
    }));

    const newInputValidation = updateInputValidation(
      inputField,
      value,
      formData,
    );

    updateInlineErrorStateWithDelay(inputField, newInputValidation);
  };

  const handleResponseErrorFlags = useCallback(
    (errorFlags: ErrorFlags) => {
      const resetForm = () => {
        setFormData({ ...getDefaultUserRegistrationData() });
        clearInputValidation();
        clearAllInlineErrors();
      };

      if (
        errorFlags.hasBasicInfoFormError ||
        errorFlags.hasUnrecoverableError
      ) {
        setIsOnBasicInfoForm(true);
      }

      if (errorFlags.hasErrorsOnBothForms || errorFlags.hasUnrecoverableError) {
        resetForm();

        setAlert({
          color: "destructive",
          message: "Oops, something went wrong.  Please start over.",
        });
      }
    },
    [setAlert, clearInputValidation, clearAllInlineErrors],
  );

  // API error when creating a new user
  useEffect(() => {
    if (!userCreationErrorResponse) return;

    const { inlineError: newInlineError, errorFlags: newApiErrorFlags } =
      parseCreateUserError(userCreationErrorResponse);

    setInlineErrorState(newInlineError);
    setApiErrorFlags(newApiErrorFlags);
    handleResponseErrorFlags(newApiErrorFlags);
  }, [
    userCreationErrorResponse,
    setInlineErrorState,
    handleResponseErrorFlags,
  ]);

  useEffect(() => {
    setShowCommonPasswordWarning(
      // Errors on both forms take precedence over common password error
      !apiErrorFlags?.hasErrorsOnBothForms &&
        !!apiErrorFlags?.hasCommonPasswordError &&
        !isOnBasicInfoForm,
    );
  }, [apiErrorFlags, setShowCommonPasswordWarning, isOnBasicInfoForm]);

  return (
    <AuthPageTemplate
      header={{
        title: content.title,
        description: content.description,
      }}
    >
      {showCommonPasswordWarning && (
        <Alert color="warning" className={styles.commonPasswordAlert}>
          That password meets the requirements. But, it could be easily guessed.
          Please create a new password that's more unique and difficult to
          guess.
        </Alert>
      )}

      {isOnBasicInfoForm ? (
        <NameAndEmailForm
          formData={formData}
          onComplete={onCompleteFirstForm}
          onCancel={() => navigate(-1)}
          onChangeInput={onChangeInput}
          inputValidation={inputValidationMap}
          inlineError={inlineErrorState}
        />
      ) : (
        <LoginCredentialsForm
          formData={formData}
          isLoading={isSubmittingForm}
          onComplete={onCompleteLoginForm}
          onCancel={() => {
            setIsOnBasicInfoForm(true);
            window.scrollTo(0, 0);
          }}
          onChangeInput={onChangeInput}
          inputValidation={inputValidationMap}
          inlineError={inlineErrorState}
        />
      )}
    </AuthPageTemplate>
  );
};

export default RegistrationForms;
